import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  extractDocumentCountryCode,
  formatIdentityDocumentLabel,
  formatIdentityProofTitle,
  IDENTITY_DOC_LABEL_ROLE,
  IDENTITY_DOC_LABEL_VARIANT,
  resolveIdentityDocumentLabelVariant,
} from "../baasIdentityDocumentLabels";
import {
  getDeclarationProofSlots,
  resolveNationalIdScheme,
} from "../baasIdentitySchemes";
import { formatDocumentTypeLabel } from "../baasEntidadeDocumentUtils";
import {
  CollapsibleSection,
  CountrySelectField,
  Field,
  StateOrTextField,
  inputClass,
} from "./baasEntidadeCreateFields";

export const BaasEntidadeDocumentDeclarationCard = ({
  doc,
  countryOptions = [],
  canEdit,
  saving,
  saveError,
  saveSuccess,
  onSave,
  asAccordion = false,
  defaultOpen = false,
  forcedCountryCode = null,
  hint = null,
  nestedContent = null,
  titleOverride = null,
  nested = false,
  existingProofByType = null,
  uploadEnabled = false,
  /** "sectionItem" = flat row inside unified section (no nested card border). */
  variant = "card",
  /** Metadata edited elsewhere (e.g. Dados pessoais); show only proof uploads. */
  proofsOnly = false,
  /** CPF vs RG vs ITIN when type is NATIONAL_ID (or CRM for REGISTRATION_ID). */
  identityVariant = null,
}) => {
  const documentType = doc.document_id || doc.documentId || doc.description;
  const lockedCountry = forcedCountryCode
    ? String(forcedCountryCode).trim().toUpperCase()
    : null;
  const [number, setNumber] = useState(doc.number || "");
  const [countryCode, setCountryCode] = useState(
    lockedCountry || String(doc.country_code || doc.countryCode || "").toUpperCase(),
  );
  const [issuingRegion, setIssuingRegion] = useState(doc.issuing_region || doc.issuingRegion || "");
  const [expirationDate, setExpirationDate] = useState(
    doc.expiration_date || doc.expirationDate || "",
  );
  const [pendingProofs, setPendingProofs] = useState({});

  useEffect(() => {
    setNumber(doc.number || "");
    setCountryCode(
      lockedCountry || String(doc.country_code || doc.countryCode || "").toUpperCase(),
    );
    setIssuingRegion(doc.issuing_region || doc.issuingRegion || "");
    setExpirationDate(doc.expiration_date || doc.expirationDate || "");
  }, [doc, lockedCountry]);

  useEffect(() => {
    if (!lockedCountry) return;
    if (String(countryCode || "").toUpperCase() === lockedCountry) return;
    setCountryCode(lockedCountry);
  }, [lockedCountry, countryCode]);

  const dirtyMeta =
    String(number || "").trim() !== String(doc.number || "").trim()
    || String(countryCode || "").toUpperCase()
      !== String(doc.country_code || doc.countryCode || "").toUpperCase()
    || String(issuingRegion || "").trim() !== String(doc.issuing_region || doc.issuingRegion || "").trim()
    || String(expirationDate || "").trim() !== String(doc.expiration_date || doc.expirationDate || "").trim();

  const pendingProofList = Object.entries(pendingProofs)
    .filter(([, file]) => Boolean(file))
    .map(([type, file]) => ({ type, file }));
  const hasPendingProofs = pendingProofList.length > 0;
  const canSave = canEdit && (dirtyMeta || hasPendingProofs);

  const docTypeKey = String(documentType || "").toUpperCase();
  const liveProofSlots = useMemo(() => {
    if (!countryCode) return [];
    return getDeclarationProofSlots(countryCode, docTypeKey, { identityVariant });
  }, [countryCode, docTypeKey, identityVariant]);

  const setProofFile = (type, file) => {
    setPendingProofs((current) => {
      if (!file) {
        const next = { ...current };
        delete next[type];
        return next;
      }
      return { ...current, [type]: file };
    });
  };

  const isSectionItem = variant === "sectionItem";
  const proofSectionClass = isSectionItem
    ? "mt-4 border-t border-gray-100 pt-4 dark:border-brand-dark-border"
    : "mt-4 rounded-lg border border-dashed border-gray-200 p-3 dark:border-brand-dark-border";

  const resolveProofHint = () => {
    if (docTypeKey === "PASSPORT") {
      return intl.get("baas.entidades.detail.passportProofHint");
    }
    if (docTypeKey === "SSN" || identityVariant === "itin") {
      return intl.get("baas.entidades.detail.taxIdDeclarationHint");
    }
    if (docTypeKey === "REGISTRATION_ID") {
      return intl.get("baas.entidades.detail.registrationIdProofHint");
    }
    if (docTypeKey === "DRIVERS_LICENSE" && String(countryCode || "").toUpperCase() === "US") {
      return intl.get("baas.entidades.detail.driversLicenseProofHintUs");
    }
    return intl.get(resolveNationalIdScheme(countryCode).locale.proofHintKey);
  };

  const fields = (
    <>
      {!proofsOnly && hint ? (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
      {!proofsOnly ? (
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={intl.get("baas.entidades.create.identity.number")} optional>
          <input
            type="text"
            className={inputClass}
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            disabled={!canEdit || saving}
            readOnly={!canEdit || saving}
          />
        </Field>
        <CountrySelectField
          label={intl.get("baas.entidades.create.identity.countryCode")}
          value={countryCode}
          onChange={setCountryCode}
          countryOptions={countryOptions}
          required
          disabled={!canEdit || saving || Boolean(lockedCountry)}
        />
        <Field label={intl.get("baas.entidades.create.identity.expirationDate")} optional>
          <input
            type="date"
            className={inputClass}
            value={expirationDate || ""}
            onChange={(event) => setExpirationDate(event.target.value)}
            disabled={!canEdit || saving}
            readOnly={!canEdit || saving}
          />
        </Field>
        <StateOrTextField
          label={intl.get("baas.entidades.create.identity.issuingRegion")}
          countryCode={countryCode}
          value={issuingRegion}
          onChange={setIssuingRegion}
          optional
          disabled={!canEdit || saving}
        />
      </div>
      ) : null}

      {liveProofSlots.length > 0 ? (
        <div className={proofSectionClass}>
          <p className="mb-1 text-sm font-medium text-brand-dark dark:text-gray-100">
            {formatIdentityProofTitle({
              documentType,
              countryCode,
              countryOptions,
              identityVariant,
            })}
          </p>
          <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
            {resolveProofHint()}
          </p>
          <p className="mb-3 text-xs font-medium text-amber-700 dark:text-amber-400">
            {intl.get("baas.entidades.detail.selfieMustMatchDocumentHint")}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {liveProofSlots.map((slot) => {
              const existing = existingProofByType?.[slot.type];
              const pending = pendingProofs[slot.type];
              const slotCountryCode =
                slot.proofCountryCode || countryCode || extractDocumentCountryCode(existing);
              const slotVariant =
                slot.variant
                || resolveIdentityDocumentLabelVariant({
                  documentType: slot.type,
                  countryCode: slotCountryCode,
                  isLinkedNationalIdProof:
                    docTypeKey === "NATIONAL_ID"
                    && resolveNationalIdScheme(countryCode).proofKind === "drivers_license",
                });
              const slotLabel = formatIdentityDocumentLabel({
                documentType: slot.type,
                countryCode: slotCountryCode,
                countryOptions,
                variant: slotVariant,
                doc: existing,
              });
              return (
                <label key={slot.type} className="block text-sm">
                  <span className="mb-1 flex items-center justify-between gap-2 text-gray-600 dark:text-gray-400">
                    <span>{slotLabel}</span>
                    {existing && !pending ? (
                      <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                        {intl.get("baas.entidades.detail.proofSlotUploaded")}
                      </span>
                    ) : null}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-gold/15 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-dark dark:text-gray-300"
                    disabled={!canEdit || !uploadEnabled || saving}
                    onChange={(event) => setProofFile(slot.type, event.target.files?.[0] || null)}
                  />
                  {pending ? (
                    <button
                      type="button"
                      className="mt-1 text-xs text-gray-500 underline dark:text-gray-400"
                      disabled={saving}
                      onClick={() => setProofFile(slot.type, null)}
                    >
                      {intl.get("baas.entidades.detail.proofSlotClear")} ({pending.name})
                    </button>
                  ) : null}
                </label>
              );
            })}
          </div>
          {!uploadEnabled && canEdit ? (
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              {intl.get("baas.entidades.detail.documentsUploadDisabledHint")}
            </p>
          ) : null}
        </div>
      ) : canEdit ? (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
          {intl.get("baas.entidades.detail.documentsUploadCountryFirstHint")}
        </p>
      ) : null}

      {saveError ? <p className="mt-2 text-sm text-red-500">{saveError}</p> : null}
      {saveSuccess ? <p className="mt-2 text-sm text-emerald-600">{saveSuccess}</p> : null}
      {canEdit ? (
        <div className="mt-3">
          <button
            type="button"
            className="btn-primary"
            disabled={!canSave || saving}
            onClick={async () => {
              const ok = await onSave?.(doc, {
                number,
                countryCode,
                issuingRegion,
                expirationDate,
                identityVariant,
                proofFiles: pendingProofList,
              });
              if (ok) setPendingProofs({});
            }}
          >
            {saving
              ? intl.get("baas.entidades.detail.savingDocument")
              : intl.get("baas.entidades.detail.saveDeclarationAndProofs")}
          </button>
        </div>
      ) : null}
      {nestedContent}
    </>
  );

  const title =
    titleOverride
    || (proofsOnly
      ? formatIdentityProofTitle({
          documentType,
          countryCode,
          countryOptions,
        })
      : formatDocumentTypeLabel(documentType, {
          countryCode,
          countryOptions,
          identityVariant,
          labelRole: IDENTITY_DOC_LABEL_ROLE.DECLARATION,
          variant:
            String(documentType || "").toUpperCase() === "DRIVERS_LICENSE"
            || String(documentType || "").toUpperCase() === "DRIVERS_LICENSE_BACK"
              ? IDENTITY_DOC_LABEL_VARIANT.STANDALONE
              : IDENTITY_DOC_LABEL_VARIANT.DEFAULT,
          doc,
        }));

  if (asAccordion) {
    return (
      <CollapsibleSection
        title={title}
        optional
        defaultOpen={Boolean(number || doc.number || doc.id || defaultOpen || hasPendingProofs)}
      >
        {fields}
      </CollapsibleSection>
    );
  }

  return (
    <div
      className={
        nested || isSectionItem
          ? "py-4 first:pt-0 last:pb-0"
          : "rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border"
      }
    >
      {!nested ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{title}</p>
          {!proofsOnly && number ? (
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{number}</span>
          ) : null}
        </div>
      ) : (
        <p className="mb-3 text-sm font-medium text-brand-dark dark:text-gray-100">{title}</p>
      )}
      {fields}
    </div>
  );
};

export default BaasEntidadeDocumentDeclarationCard;
